import { useContext, useEffect, useState } from "react";
import { EmployeeDashboardWrapper } from "./styled";
import { H1, H2, Label } from "../../../../components/typography/styled";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseSelect } from "../../../../components/form/select/styled";
import { ResetPasswordModal } from "../../../../containers/app/modals/resetpasswordmodal";
import Cookies from "universal-cookie";
import { getEmployeePayslips } from "../../../../utils/apis/payroll/getEmployeePayslips";
import { PayslipDetailsModal } from "../../../../containers/app/modals/payslipdetailsmodal";
import { getYearRange } from "../../../../helpers/retrieveAllYearsToDate";
import { months } from "../../../../helpers/retrieveAllMonths";
import { Context } from "../../../../context";
import { Table } from "../../../../components/table";
import { getUser } from "../../../../utils/apis/user/getUser";
import { Row } from "../../../../components/flex/styled";

export const EmployeeDashboard = () => {
  const PAGE_SIZE = 10;
  const startDate = 2020;
  const endDate = new Date().getFullYear();

  const cookies = new Cookies();
  const TOKEN = cookies.get("TOKEN");
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();

  const { isPayslipDetailsModalOpen, setIsPayslipDetailsModalOpen } =
    useContext(Context);

  const [payslips, setPayslips] = useState([]);
  const [selectedPayslipId, setSelectedPayslipId] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState({
    month: 0,
    year: currentYear,
  });

  useEffect(() => {
    const fetchPayslips = async () => {
      try {
        const res = await getEmployeePayslips(TOKEN);
        let filteredPayslips = res?.data || [];
        filteredPayslips = filteredPayslips.filter((payslip) => {
          const matchMonth = filter.month
            ? payslip.month === Number(filter.month)
            : true;
          const matchYear = filter.year
            ? payslip.year === Number(filter.year)
            : true;
          return matchMonth && matchYear;
        });
        setPayslips(filteredPayslips);
      } catch (err) {
        console.error("Failed to fetch employee payslips:", err);
      }
    };
    fetchPayslips();
  }, [TOKEN, filter]);

  useEffect(() => {
    getUser(TOKEN)
      .then((data) => {
        setLoggedInUser(data);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [TOKEN]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentPage(1);
    setFilter((prev) => ({
      ...prev,
      [name]: Number(value),
    }));
  };

  const handleOpenPayslipModal = async (e, payslipId) => {
    e.stopPropagation();
    if (!String(payslipId).trim()) return;
    setSelectedPayslipId(payslipId);
    return !isPayslipDetailsModalOpen && setIsPayslipDetailsModalOpen(true);
  };

  const totalPayslips = payslips.length;
  const totalPages = Math.max(1, Math.ceil(totalPayslips / PAGE_SIZE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const start = (currentPage - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  const paginatedPayslips = payslips.slice(start, end);
  const rangeStart = totalPayslips === 0 ? 0 : start + 1;
  const rangeEnd = Math.min(end, totalPayslips);
  const payslipCountText = totalPayslips === 1 ? "payslip" : "payslips";

  const getVisiblePageNumbers = () => {
    if (totalPages <= 3) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
    if (currentPage <= 2) {
      return [1, 2, 3];
    }
    if (currentPage >= totalPages - 1) {
      return [totalPages - 2, totalPages - 1, totalPages];
    }
    return [currentPage - 1, currentPage, currentPage + 1];
  };

  const visiblePageNumbers = getVisiblePageNumbers();

  return (
    <EmployeeDashboardWrapper>
      <div className="title-heading">
        <H1>Welcome, {loggedInUser?.fullName?.split(" ")[1]}</H1>
        <H2>Payslip List</H2>
      </div>
      <Row className="filter">
        <BaseFieldSet>
          <Label>Month</Label>
          <BaseSelect
            name="month"
            onChange={(e) => handleChange(e, "payroll")}
            value={filter.month}
          >
            <option value={0}>Select Month</option>
            {months.map((month, index) => {
              return (
                <option key={index} value={index + 1}>
                  {month}
                </option>
              );
            })}
          </BaseSelect>
        </BaseFieldSet>
        <BaseFieldSet>
          <Label>Year</Label>
          <BaseSelect
            name="year"
            onChange={(e) => handleChange(e)}
            value={filter.year}
          >
            {getYearRange(startDate, endDate).map((year, index) => {
              return (
                <option key={index} value={year}>
                  {year}
                </option>
              );
            })}
          </BaseSelect>
        </BaseFieldSet>
      </Row>
      <div className="table">
        <Table
          location={"Employee Payslip Table"}
          columnTitles={["Month", "Year", "Action"]}
          rowItems={paginatedPayslips}
          handleRowItemClick={handleOpenPayslipModal}
        />
        <div className="table-footer">
          <span>
            Showing {rangeStart}-{rangeEnd} of {totalPayslips}{" "}
            {payslipCountText}
          </span>
          <div className="pagination" aria-hidden="true">
            <button
              type="button"
              className="page-chip"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            >
              &lt;
            </button>
            {visiblePageNumbers.map((pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                className={`page-chip ${currentPage === pageNumber ? "active" : ""}`}
                onClick={() => setCurrentPage(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
            <button
              type="button"
              className="page-chip"
              disabled={currentPage === totalPages}
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
            >
              &gt;
            </button>
          </div>
        </div>
      </div>

      <ResetPasswordModal width={"40%"} height={"60%"} />
      <PayslipDetailsModal
        width={"60%"}
        height={"55%"}
        payslipId={selectedPayslipId}
      />
    </EmployeeDashboardWrapper>
  );
};
